import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import styles from "./GiftClaim.module.css";
import Api from "../../utils/Api/Api";
import { track as trackEvent } from "../../utils/analyticsService";
import { longDate } from "../../utils/format";
import { useAuthGate, hasSession } from "../../components/AuthModal/AuthGate";
import { Sheet } from "../../components/Sheet/Sheet";
import { isDiwaliGift, whatsAppUrl } from "../../utils/gift";
import { isIndia } from "../../utils/market";
import { endSession } from "../../utils/session";
import FleekIcon from "../../asset/icons/Brand_logo_Sq.png";

/*
 * Where a gift recipient lands from the link: web.getfleek.app/gift/<token>.
 *
 * Signing in uses the auth sheet, so the page never unmounts and the claim
 * runs straight after the code is verified. If the session turns out to be
 * stale, App's 401 handler sends the browser to /login instead; for that case
 * the page leaves two markers before posting - the app's existing
 * returnToPage (VerifyNumber navigates to it after sign-in) and a per-tab
 * AUTO_CLAIM_KEY naming this token - and on the way back it claims once,
 * removing the marker before it posts so a reload cannot post twice.
 */
const AUTO_CLAIM_KEY = "fleek_gift_autoclaim";

const readStore = (store, key) => {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};
const writeStore = (store, key, value) => {
  try {
    if (value == null) store.removeItem(key);
    else store.setItem(key, value);
  } catch {
    /* storage blocked: the sheet flow does not need it */
  }
};

/*
 * App's axios interceptor resolves failed requests with the error instead of
 * rejecting, so a 404 arrives here as a "successful" value. Read both shapes.
 */
const outcome = (res) => {
  const failed = res instanceof Error || res?.isAxiosError;
  const response = failed ? res.response : res;
  return {
    ok: !failed && !!response && response.status >= 200 && response.status < 300,
    status: response?.status || 0,
    data: response?.data || null,
  };
};

const firstName = (name) => String(name || "").trim().split(" ")[0];

const durationText = (plan = {}) => {
  const n = Number(plan.duration);
  const unit = String(plan.duration_type || "").trim().toLowerCase();
  if (!n || !unit) return "";
  const singular = unit.replace(/s$/, "");
  return `${n} ${n === 1 ? singular : `${singular}s`}`;
};

const isAndroid = () => typeof navigator !== "undefined" && /android/i.test(navigator.userAgent || "");

const track = (event, params = {}) => {
  try {
    trackEvent(event, params);
  } catch {
    /* analytics must never block the claim */
  }
};

/** Find the claimed gift among the viewer's orders, by its token. */
const findGiftOrder = async (token) => {
  const { ok, data } = outcome(await Api.GetMyOrdersNow().catch((e) => e));
  if (!ok) return null;
  const rows = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
  return rows.find((o) => o?.gift?.token === token && o?.gift?.role === "recipient") || null;
};

const GiftIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 12v9H4v-9" />
    <path d="M2 7h20v5H2z" />
    <path d="M12 21V7" />
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
  </svg>
);

const Diya = () => (
  <svg className={styles.diya} width="40" height="34" viewBox="0 0 40 34" aria-hidden="true">
    <path d="M20 2c3 5 5 8 5 11a5 5 0 0 1-10 0c0-3 2-6 5-11z" fill="#ffc747" />
    <path d="M20 8c1.4 2.4 2.2 4 2.2 5.4a2.2 2.2 0 0 1-4.4 0c0-1.4.8-3 2.2-5.4z" fill="#ff8a66" />
    <path d="M3 20h34c-1.5 7-8.5 12-17 12S4.5 27 3 20z" fill="#e8a317" />
    <path d="M3 20h34" stroke="#ffc747" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const GiftClaim = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { requireAuth } = useAuthGate();

  const [phase, setPhase] = useState("loading"); // loading | ready | not_found | load_error
  const [gift, setGift] = useState(null);
  // What the claim attempt ended in, when it did not end on an order.
  // One of: claimed | expired | not_ready | own_gift | inline
  const [result, setResult] = useState(null);
  const [inlineError, setInlineError] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [copied, setCopied] = useState(false);
  // own_gift: the buyer opened their own link. The claim's error response
  // carries the sender's view of the gift (share_message among it).
  const [ownGift, setOwnGift] = useState(null);
  const [confirmKeep, setConfirmKeep] = useState(false);
  const [keeping, setKeeping] = useState(false);
  const [keepError, setKeepError] = useState("");
  const [kept, setKept] = useState(false);
  const inFlight = useRef(false);
  const viewed = useRef(false);
  // The auth sheet runs the claim from a closure made at click time; the
  // gift is read through a ref so that closure never sees a stale one.
  const giftRef = useRef(null);

  useEffect(() => {
    const previous = document.title;
    document.title = "You've got a gift | Fleek";
    return () => {
      document.title = previous;
    };
  }, []);

  const goToOrder = useCallback(
    (order, data) => {
      const state = {
        giftClaimed: {
          sender_name: data?.sender_name || order?.gift?.sender_name || "",
          brand: data?.plan?.brand || order?.subscription_plan?.service_name || "",
        },
      };
      if (order?.id) navigate(`/orderdetail/${order.id}`, { replace: true, state });
      else navigate("/myorder", { replace: true, state });
    },
    [navigate]
  );

  const claim = useCallback(
    async (source) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setClaiming(true);
      setInlineError("");

      // Markers for the stale-session path (see top of file).
      writeStore(sessionStorage, AUTO_CLAIM_KEY, token);
      writeStore(localStorage, "returnToPage", `/gift/${token}`);

      const { ok, status, data } = outcome(await Api.ClaimGift(token).catch((e) => e));

      if (status === 401) {
        // App's interceptor is already sending the browser to /login; the
        // markers bring it back here to claim.
        return;
      }
      writeStore(sessionStorage, AUTO_CLAIM_KEY, null);
      writeStore(localStorage, "returnToPage", null);

      const current = giftRef.current;
      const brand = current?.plan?.brand || "";
      if (ok) {
        track("gift_claimed", { gift_theme: current?.theme, brand, claim_source: source });
        const order = await findGiftOrder(token);
        goToOrder(order, current);
        return;
      }

      const code = data?.error || (status ? `http_${status}` : "network_error");
      track("gift_claim_failed", { error_code: code, gift_theme: current?.theme, brand, claim_source: source });
      inFlight.current = false;
      setClaiming(false);

      switch (code) {
        case "already_claimed":
          setResult("claimed");
          break;
        case "returned":
        case "expired":
          setResult("expired");
          break;
        case "not_ready":
          setResult("not_ready");
          break;
        case "not_found":
          setPhase("not_found");
          break;
        case "own_gift":
          setOwnGift(data);
          setResult("own_gift");
          break;
        default:
          setResult("inline");
          setInlineError(data?.detail || "We couldn't claim this gift just now. Try again.");
      }
    },
    [token, goToOrder]
  );

  const load = useCallback(async () => {
    setPhase("loading");
    setResult(null);
    setInlineError("");
    const { ok, status, data } = outcome(await Api.GetGift(token).catch((e) => e));
    if (!ok) {
      setPhase(status === 404 ? "not_found" : "load_error");
      if (status === 404 && !viewed.current) {
        viewed.current = true;
        track("gift_page_viewed", { gift_state: "not_found" });
      }
      return;
    }
    giftRef.current = data;
    setGift(data);
    if (!viewed.current) {
      viewed.current = true;
      track("gift_page_viewed", {
        gift_state: data?.state,
        gift_theme: data?.theme,
        brand: data?.plan?.brand || "",
        signed_in: hasSession(),
      });
    }

    // Already claimed and this viewer is the one who claimed it: take them
    // to it. A GET, so opening the link again never posts anything.
    if (data?.state === "claimed" && hasSession()) {
      const order = await findGiftOrder(token);
      if (order) {
        goToOrder(order, data);
        return;
      }
    }
    setPhase("ready");

    // Back from a full-page sign-in with a claim pending: claim once.
    if (data?.state === "ready" && hasSession() && readStore(sessionStorage, AUTO_CLAIM_KEY) === token) {
      writeStore(sessionStorage, AUTO_CLAIM_KEY, null);
      claim("resume");
    }
  }, [token, goToOrder, claim]);

  useEffect(() => {
    load();
  }, [load]);

  const onClaimClick = () => {
    const signedIn = hasSession();
    track("gift_claim_clicked", { gift_theme: gift?.theme, brand: gift?.plan?.brand || "", signed_in: signedIn });
    requireAuth(() => claim("button"), "Sign in to claim your gift.");
  };

  const shareLink = gift?.links?.web || window.location.href;
  const copyLink = () => {
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(shareLink).then(done, () => {});
    }
  };

  const shareOnWhatsApp = () => {
    track("gift_shared", { via: "whatsapp", context: "own_gift" });
    window.open(whatsAppUrl(ownGift?.share_message || shareLink), "_blank", "noopener,noreferrer");
  };

  // Signed in as the buyer, the link can only ever land here. Sign out and
  // come back to the same link, so another number can sign in and claim it.
  const claimWithAnotherAccount = () => {
    track("gift_switch_account", { gift_theme: gift?.theme, brand: gift?.plan?.brand || "" });
    endSession("gift_switch_account");
    window.location.href = `/gift/${token}`;
  };

  const keep = async () => {
    if (keeping) return;
    setConfirmKeep(false);
    setKeeping(true);
    setKeepError("");
    const { ok, data } = outcome(await Api.KeepGift(token).catch((e) => e));
    setKeeping(false);
    if (ok) {
      track("gift_kept", { theme: gift?.theme || null, context: "own_gift" });
      setKept(true);
      return;
    }
    setKeepError(data?.detail || "We couldn't do that. Try again.");
    // Claimed or returned in the meantime: show what it is now.
    if (data?.error === "not_keepable") load();
  };

  const plan = gift?.plan || {};
  // Plain unless the gift was created with the older Diwali theme.
  const diwali = isDiwaliGift(gift?.theme);
  const sender = firstName(gift?.sender_name) || "Someone";
  const recipient = firstName(gift?.recipient_name);
  const brand = plan.brand || "a plan";
  const duration = durationText(plan);
  const playLink = gift?.links?.play;

  // The state to show: a claim attempt's outcome wins over what the page loaded with.
  let view = phase;
  if (phase === "ready") {
    if (kept) view = "kept";
    else if (result && result !== "inline") view = result;
    else if (gift?.state === "claimed") view = "claimed";
    else if (gift?.state === "returned" || gift?.state === "expired") view = "expired";
    else if (gift?.state === "awaiting_payment") view = "not_ready";
    else view = "gift";
  }

  const status = {
    not_found: {
      title: "This gift link isn't valid",
      body: "Check the link you were sent, or ask the person who sent it to share it again.",
    },
    load_error: {
      title: "We couldn't open this gift",
      body: "Check your connection and try again.",
      retry: "Try again",
    },
    claimed: {
      title: "This gift has been claimed",
      body: "Someone has already claimed it. If that was you, sign in with the same number to see it in your orders.",
    },
    expired: {
      title: "This gift link has expired",
      body: `It wasn't claimed in time, so it went back to ${firstName(gift?.sender_name) || "the person who sent it"}.`,
    },
    not_ready: {
      title: "Your gift isn't ready yet",
      body: "It's still being wrapped. Try again in a minute.",
      retry: "Try again",
    },
    kept: {
      title: "It's yours",
      body: "The gift link no longer works. The plan is in your orders.",
    },
  }[view];

  return (
    <div className={`${styles.page} ${diwali ? styles.diwali : ""}`}>
      <div className={styles.shell}>
        <img className={styles.wordmark} src={FleekIcon} alt="Fleek" />

        {view === "loading" && <div className={styles.skeleton} aria-busy="true" aria-label="Loading your gift" />}

        {status && (
          <section className={styles.statusCard}>
            <span className={styles.statusIcon}>
              <GiftIcon size={26} />
            </span>
            <h1 className={styles.statusTitle}>{status.title}</h1>
            <p className={styles.statusBody}>{status.body}</p>
            {status.retry && (
              <button type="button" className={styles.secondary} onClick={load}>
                {status.retry}
              </button>
            )}
            {view === "kept" && (
              <button type="button" className={styles.secondary} onClick={() => navigate("/myorder")}>
                View my orders
              </button>
            )}
            {view === "claimed" && !hasSession() && (
              // Signing in re-reads the gift; if this viewer claimed it, load()
              // finds it in their orders and goes there.
              <button type="button" className={styles.secondary} onClick={() => requireAuth(load, "Sign in to see your gift.")}>
                Sign in
              </button>
            )}
            <button type="button" className={styles.textButton} onClick={() => navigate("/marketplace")}>
              Explore Fleek
            </button>
          </section>
        )}

        {view === "own_gift" && (
          <section className={styles.statusCard}>
            <span className={styles.statusIcon}>
              <GiftIcon size={26} />
            </span>
            <h1 className={styles.statusTitle}>You sent this gift</h1>
            <p className={styles.statusBody}>
              You're signed in as {gift?.sender_name ? firstName(gift.sender_name) : "the account that bought it"}
              {gift?.sender_name ? ", who bought it" : ""}, so it can't be claimed here. Share the link with{" "}
              {recipient || "the person it's for"} — anyone else who opens it and signs in can claim it.
            </p>
            <div className={styles.linkRow}>
              <span className={styles.linkText}>{shareLink}</span>
              <button type="button" className={styles.copy} onClick={copyLink}>
                {copied ? "Copied" : "Copy link"}
              </button>
            </div>
            <button type="button" className={styles.primary} onClick={shareOnWhatsApp}>
              Send on WhatsApp
            </button>
            <button type="button" className={styles.secondary} onClick={claimWithAnotherAccount}>
              Claim with a different account
            </button>
            {keepError && (
              <p className={styles.error} role="alert">
                {keepError}
              </p>
            )}
            <button type="button" className={styles.quiet} onClick={() => setConfirmKeep(true)} disabled={keeping}>
              {keeping ? "Keeping…" : "Keep it for myself"}
            </button>
          </section>
        )}

        {view === "gift" && (
          <section className={styles.card}>
            <div className={styles.hero}>
              {diwali && <Diya />}
              <p className={styles.eyebrow}>
                {diwali ? "A Diwali gift" : "A gift"}
                {recipient ? ` for ${recipient}` : ""}
              </p>
              <h1 className={styles.headline}>
                {sender} sent you {brand}
              </h1>
            </div>

            <div className={styles.body}>
              <div className={styles.planRow}>
                <img
                  className={styles.logo}
                  src={plan.logo_url || FleekIcon}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.src = FleekIcon;
                  }}
                />
                <div className={styles.planText}>
                  <div className={styles.brand}>{plan.brand}</div>
                  {(plan.plan_name || duration) && (
                    <div className={styles.planName}>{[plan.plan_name, duration].filter(Boolean).join(" · ")}</div>
                  )}
                </div>
              </div>

              {gift?.message && (
                <blockquote className={styles.message}>
                  <p>{gift.message}</p>
                  <footer>— {sender}</footer>
                </blockquote>
              )}

              {inlineError && (
                <p className={styles.error} role="alert">
                  {inlineError}
                </p>
              )}

              <button type="button" className={styles.primary} onClick={onClaimClick} disabled={claiming}>
                {claiming ? "Claiming…" : "Claim your gift"}
              </button>
              <p className={styles.fine}>
                {hasSession() ? "It goes straight to your Fleek orders." : isIndia() ? "Sign in with your phone number to claim it." : "Sign in to claim it."}
                {gift?.returns_at ? ` Claim by ${longDate(gift.returns_at)}.` : ""}
              </p>
            </div>
          </section>
        )}

        {playLink && isAndroid() && view !== "loading" && (
          <a className={styles.appLink} href={playLink}>
            On Android? Get the Fleek app
          </a>
        )}
      </div>

      <Sheet open={confirmKeep} onClose={() => setConfirmKeep(false)} label="Keep this gift?">
        <p className={styles.sheetTitle}>Keep this gift?</p>
        <p className={styles.sheetBody}>The link stops working and the plan becomes yours. This can't be undone.</p>
        <button type="button" className={styles.primary} onClick={keep}>
          Keep it
        </button>
        <button type="button" className={styles.textButton} onClick={() => setConfirmKeep(false)}>
          Cancel
        </button>
      </Sheet>
    </div>
  );
};

export default GiftClaim;
